// Inspiration: https://codepen.io/shshaw/pen/9e810322d70c306de2d18237d0cb2d78
import { memo, useLayoutEffect, useEffect, useRef, useState, useCallback } from 'react'
import { useContext } from 'use-context-selector'
import classNames from 'classnames'
import gsap from 'gsap'
import { Lethargy } from 'lethargy'
import { useWheel } from '@use-gesture/react'
import injectSheet from 'react-jss'
import usePrevious from '@/hooks/usePrevious'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { clamp } from '@/utils/math'
import { ensurePrevPlayedSegments } from '@/utils/media'
import style from './style'

const lethargy = new Lethargy()

const ProductVideo = ({
  classes,
  zoom, // Zoom Video Url
  rotation, // Rotation Video Url
  poster, // Poster Image Url
  isRotationFinished,
  setRotationFinished,
  setToolbarVisible,
  scrollSection,
  setScrollSection,
  overviewSection,
  isOverviewInPage,
  sectionsNumber,
  scaleVideo,
  isOverviewExited,
  visible,
}) => {
  const { scrollbar, scroll } = useContext(ScrollbarContext)
  const [isAnimating, setAnimating] = useState(false)
  const $rotation = useRef()
  const $zoom = useRef()

  /*------------------------------
  Manage step scroll
  ------------------------------*/
  const prevSection = usePrevious(scrollSection)
  useLayoutEffect(() => {
    if (prevSection !== -1) {
      const fractions = $zoom.current.duration / sectionsNumber || 0
      gsap.to($zoom.current, {
        onStart: () => setAnimating(true),
        currentTime: fractions * scrollSection,
        duration: fractions,
        ease: 'linear',
        onComplete: () => setAnimating(false), // Reset isAnimating
      })

      // Unlock the page after last step
      if (scrollSection === (sectionsNumber + 1) && !isOverviewExited) {
        scrollbar.scrollTo('#description', {
          duration: 1000,
          easings: [0.25, 1, 0.5, 1],
        })
      }
    }
  }, [scrollSection])

  useEffect(() => {
    if ($zoom.current) ensurePrevPlayedSegments(zoom, $zoom.current)
  }, [$zoom])

  /*------------------------------
  Scale video on enter/exit video
  ------------------------------*/
  useLayoutEffect(() => {
    if ($zoom.current) {
      gsap.killTweensOf($zoom.current)
      gsap.to($zoom.current, {
        duration: 2,
        scale: scaleVideo,
        ease: 'power4.out',
      })
    }
  }, [scaleVideo])

  /*------------------------------
  Seek video to last step when overview div is outside
  ------------------------------*/
  useEffect(() => {
    if (isOverviewExited) setScrollSection(sectionsNumber + 1)
  }, [isOverviewExited])

  /*------------------------------
  Manage Overview section is in page
  ------------------------------*/
  useLayoutEffect(() => {
    if (isOverviewInPage && scroll.current.y > 0) {
      scrollbar.stop()
      scrollbar.scrollTo(0, {
        duration: 1000,
        easings: [0.25, 1, 0.5, 1],
        callback: () => setAnimating(false),
      })
      setScrollSection(sectionsNumber)
      setAnimating(true)
    }
    if (!isOverviewInPage && scrollbar) scrollbar.start()
  }, [isOverviewInPage, scrollbar])

  /*------------------------------
  Seek zoom video with scroll
  ------------------------------*/
  const requestRef = useRef()
  /*------------------------------
  Handle almost end of rotation video
  ------------------------------*/
  const animate = useCallback(() => {
    if ($rotation.current?.currentTime > $rotation.current?.duration - 1) setToolbarVisible(true)
    requestRef.current = requestAnimationFrame(animate)
  }, [])

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(requestRef.current)
  }, [])

  /*------------------------------
  Pause video on page ready
  ------------------------------*/
  useEffect(() => {
    if (visible && $rotation.current) $rotation.current.currentTime = 0
  }, [visible])

  /*------------------------------
  Handle end of rotation video
  ------------------------------*/
  const handleEndRotation = useCallback(() => setRotationFinished(true), [])

  /*------------------------------
  Play video when page is ready
  ------------------------------*/
  useEffect(() => {
    if (visible && $rotation.current && scrollbar) {
      /*------------------------------
      Stop scroll until rotation is finished
      ------------------------------*/
      scrollbar.stop()
      $rotation.current.play()
      $rotation.current.addEventListener('ended', handleEndRotation)
    }
    return () => {
      if (visible && $rotation.current) $rotation.current.removeEventListener('ended', handleEndRotation)
    }
  }, [visible, scrollbar])

  /*------------------------------
  Manage Step
  ------------------------------*/
  const handleStep = useCallback((index) => {
    if (isAnimating) return
    setScrollSection(index)
  }, [isAnimating])

  /*------------------------------
  Step scroll events
  ------------------------------*/
  useWheel(({ event, last, memo: wait = false }) => {
    if (!last) {
      const s = lethargy.check(event)
      if (s) {
        if (!wait) {
          const slide = clamp(scrollSection - s, 0, sectionsNumber + 1)
          handleStep(slide)
          return true
        }
      } else return false
    } else return false
    return true
  },
  {
    target: overviewSection,
    enabled: isOverviewInPage,
  })

  return (
    <div className={classes.root}>
      {zoom && (
        <div
          className={classNames({
            [classes.videoWrapper]: true,
            [classes.visible]: true,
          })}
        >
          <video
            ref={$zoom}
            playsInline={true}
            webkitplaysinline="webkit-plays-inline"
            muted
            autobuffer="autobuffer"
            preload="preload"
          >
            <source
              type='video/mp4;codecs="avc1.42E01E, mp4a.40.2"'
              src={zoom}
            />
          </video>
        </div>
      )}
      {rotation && (
        <div
          className={classNames({
            [classes.videoWrapper]: true,
            [classes.visible]: !isRotationFinished,
          })}
        >
          <video
            ref={$rotation}
            playsInline={true}
            webkitplaysinline="webkit-plays-inline"
            muted
            poster={poster}
            autobuffer="autobuffer"
            preload="preload"
          >
            <source
              type='video/mp4;codecs="avc1.42E01E, mp4a.40.2"'
              src={rotation}
            />
          </video>
        </div>
      )}
    </div>
  )
}

export default injectSheet(style)(memo(ProductVideo))
